import { useEffect, useState } from "react";
import {
  Alert, Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText, Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {Add, ArrowBack, ArrowDownward, ArrowUpward, DeleteOutlineOutlined, Edit } from "@mui/icons-material";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import ContentBox from "../ContentBox/ContentBox";
import LegalbirdLoader from "../ContentLoader/LegalbirdLoader";
import LegalbirdIoConfirm from "../Modal/LegalbirdIoConfirm";
import PagePagination from "../PagePagination/PagePagination";
import { ReleaseUpdate, ReleaseUpdatePublication } from "../../types/ReleaseUpdate";
import {
  deleteReleaseUpdateMessage,
  fetchOrCreateReleaseUpdateDraft,
  fetchReleaseUpdateManagementPublication,
  fetchReleaseUpdateManagementPublications,
  fetchReleaseUpdateMediaObjects,
  getReleaseUpdateMediaObjectIds,
  publishReleaseUpdate,
  RELEASE_UPDATE_PUBLICATIONS_PER_PAGE,
  reorderReleaseUpdateMessages,
  updateReleaseUpdatePublicationDraftVisibility,
} from "../../services/releaseUpdateService";
import { useReleaseUpdateDialog } from "./ReleaseUpdateDialogProvider";
import ReleaseUpdateMessageForm from "./ReleaseUpdateMessageForm";
import ReleaseUpdateContent from "./ReleaseUpdateContent";
import releaseUpdateStyles from "./releaseUpdatesStyles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "../../provider/CurrentUserProvider";

type MessageFormState = {
  publication: ReleaseUpdatePublication;
  message: ReleaseUpdate | null;
  isPublished: boolean;
};

type DeleteState = {
  publication: ReleaseUpdatePublication;
  message: ReleaseUpdate;
} | null;

export default function ReleaseUpdateManagement() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [draft, setDraft] = useState<ReleaseUpdatePublication | null>(null);
  const [published, setPublished] = useState<ReleaseUpdatePublication[]>([]);
  const [publishedPage, setPublishedPage] = useState(1);
  const [publishedTotalPages, setPublishedTotalPages] = useState(0);
  const [selectedPublication, setSelectedPublication] = useState<ReleaseUpdatePublication | null>(null);
  const [messageForm, setMessageForm] = useState<MessageFormState | null>(null);
  const [deleteState, setDeleteState] = useState<DeleteState>(null);
  const [reviewedDraft, setReviewedDraft] = useState<ReleaseUpdatePublication | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVisibilitySaving, setIsVisibilitySaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { openReleaseUpdatePreview } = useReleaseUpdateDialog();
  const selectedMediaObjectIds = getReleaseUpdateMediaObjectIds(selectedPublication ? [selectedPublication] : []);
  const { data: selectedMediaObjects = [] } = useQuery({
    queryKey: ["releaseUpdateManagementMediaObjects", selectedMediaObjectIds],
    queryFn: () => fetchReleaseUpdateMediaObjects(selectedMediaObjectIds),
  });

  const loadDraft = async () => {
    const currentDraft = await queryClient.fetchQuery({
      queryKey: ["releaseUpdateDraft"],
      queryFn: () => fetchOrCreateReleaseUpdateDraft(currentUser["@id"]),
      staleTime: 0,
    });
    setDraft(currentDraft);

    return currentDraft;
  };

  const loadPublished = async (page = publishedPage) => {
    const publicationResult = await fetchReleaseUpdateManagementPublications(page);
    setPublished(publicationResult["hydra:member"]);
    setPublishedTotalPages(
      Math.ceil(publicationResult["hydra:totalItems"] / RELEASE_UPDATE_PUBLICATIONS_PER_PAGE),
    );
  };

  useEffect(() => {
    loadDraft()
      .catch(() => enqueueSnackbar("Der gemeinsame Entwurf konnte nicht geladen werden.", { variant: "error" }))
      .finally(() => setIsLoading(false));
  }, []);

  const showMutationError = (error: unknown, fallback: string) => {
    const text = error instanceof Error ? error.message : fallback;
    enqueueSnackbar(text, { variant: "error" });
  };

  const handleTabChange = async (newValue: number) => {
    setSelectedTab(newValue);
    setSelectedPublication(null);
    setIsLoading(true);
    try {
      if (newValue === 0) {
        await loadDraft();
      } else {
        await loadPublished();
      }
    } catch (error) {
      showMutationError(error, "Die Release-Updates konnten nicht geladen werden.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublishedPageChange = async (page: number) => {
    setIsLoading(true);
    try {
      await loadPublished(page);
      setPublishedPage(page);
    } catch (error) {
      showMutationError(error, "Die Release-Updates konnten nicht geladen werden.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVisibilityChange = async (visibleToInternal: boolean, visibleToExternal: boolean) => {
    if (!draft) {
      return;
    }
    setIsVisibilitySaving(true);
    try {
      setDraft(
        await updateReleaseUpdatePublicationDraftVisibility(
          draft,
          visibleToInternal,
          visibleToExternal,
          currentUser["@id"],
        ),
      );
    } catch (error) {
      showMutationError(error, "Die Sichtbarkeit konnte nicht gespeichert werden.");
      try {
        await loadDraft();
      } catch (reloadError) {
        showMutationError(reloadError, "Der aktuelle Entwurf konnte nicht neu geladen werden.");
      }
    } finally {
      setIsVisibilitySaving(false);
    }
  };

  const handleMoveMessage = async (index: number, offset: number) => {
    if (!draft) {
      return;
    }
    const ids = draft.messages.map((message) => message.id);
    const targetIndex = index + offset;
    [ids[index], ids[targetIndex]] = [ids[targetIndex], ids[index]];
    try {
      setDraft(await reorderReleaseUpdateMessages(draft, ids, currentUser["@id"]));
    } catch (error) {
      showMutationError(error, "Die Reihenfolge konnte nicht gespeichert werden.");
      try {
        await loadDraft();
      } catch (reloadError) {
        showMutationError(reloadError, "Der aktuelle Entwurf konnte nicht neu geladen werden.");
      }
    }
  };

  const handleDeleteMessage = async () => {
    if (!deleteState) {
      return;
    }
    let updatedPublication: ReleaseUpdatePublication;
    try {
      updatedPublication = await deleteReleaseUpdateMessage(
        deleteState.publication,
        deleteState.message,
        currentUser["@id"],
      );
    } catch (error) {
      showMutationError(error, "Die Meldung konnte nicht gelöscht werden.");
      return;
    }

    if (deleteState.publication.publishedAt) {
      setSelectedPublication(updatedPublication);
      try {
        await loadPublished();
      } catch (error) {
        showMutationError(error, "Die Meldung wurde gelöscht, die Übersicht konnte aber nicht neu geladen werden.");
      }
    } else {
      try {
        await loadDraft();
      } catch (error) {
        showMutationError(error, "Die Meldung wurde gelöscht, der Entwurf konnte aber nicht neu geladen werden.");
      }
    }
  };

  const handleReviewPublication = async () => {
    try {
      const currentDraft = await loadDraft();
      if (currentDraft.messages.length === 0) {
        enqueueSnackbar("Mindestens eine Meldung ist erforderlich.", { variant: "error" });
        return;
      }
      if (!currentDraft.visibleToInternal && !currentDraft.visibleToExternal) {
        enqueueSnackbar("Mindestens eine Sichtbarkeit ist erforderlich.", { variant: "error" });
        return;
      }
      setReviewedDraft(currentDraft);
    } catch (error) {
      showMutationError(error, "Der gemeinsame Entwurf konnte nicht geladen werden.");
    }
  };

  const handlePublish = async () => {
    if (!reviewedDraft) {
      return;
    }
    try {
      await publishReleaseUpdate(reviewedDraft, currentUser["@id"]);
    } catch (error) {
      showMutationError(error, "Das Release-Update konnte nicht veröffentlicht werden.");
      return;
    }

    enqueueSnackbar("Das Release-Update wurde veröffentlicht.", { variant: "success" });
    setSelectedTab(1);
    setSelectedPublication(null);
    setPublishedPage(1);
    try {
      await loadDraft();
      await loadPublished(1);
    } catch (error) {
      showMutationError(error, "Das Release-Update wurde veröffentlicht, die Ansicht konnte aber nicht neu geladen werden.");
    }
  };

  const handleOpenPublished = async (publicationId: number) => {
    setIsLoading(true);
    try {
      setSelectedPublication(await fetchReleaseUpdateManagementPublication(publicationId));
    } catch (error) {
      showMutationError(error, "Das Release-Update konnte nicht geladen werden.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMessageSaved = async () => {
    try {
      if (messageForm?.isPublished) {
        const refreshedPublication = await fetchReleaseUpdateManagementPublication(messageForm.publication.id);
        setSelectedPublication(refreshedPublication);
        await loadPublished();
        return;
      }
      await loadDraft();
    } catch (error) {
      showMutationError(error, "Die Meldung wurde gespeichert, die Ansicht konnte aber nicht neu geladen werden.");
    }
  };

  if (isLoading) {
    return <LegalbirdLoader centered />;
  }

  return (
    <ContentBox>
      <Tabs value={selectedTab} onChange={(_, value: number) => handleTabChange(value)}>
        <Tab label="Aktueller Entwurf" />
        <Tab label="Veröffentlicht" />
      </Tabs>
      <Divider sx={{ marginBottom: "1.5rem" }} />

      {selectedTab === 0 && draft && (
        <>
          <Typography variant="h2" sx={releaseUpdateStyles.headline}>Nächstes Release-Update</Typography>
          <Typography sx={releaseUpdateStyles.subline}>Alle PMs sammeln hier ihre Meldungen. Gespeicherte Meldungen werden gemeinsam veröffentlicht.</Typography>
          {draft.messages.length === 0 ? (
            <Alert severity="info" sx={{ marginTop: "1rem" }}>
              Der gemeinsame Entwurf enthält noch keine Meldungen.
            </Alert>
          ) : (
            <List>
              {draft.messages.map((message, index) => (
                <ListItem
                  key={message.id}
                  disablePadding
                  sx={{ padding: "0.5rem 0"}}
                  divider
                  secondaryAction={
                    <>
                      <IconButton
                        title="Nach oben"
                        disabled={index === 0}
                        onClick={() => handleMoveMessage(index, -1)}
                      >
                        <ArrowUpward />
                      </IconButton>
                      <IconButton
                        title="Nach unten"
                        disabled={index === draft.messages.length - 1}
                        onClick={() => handleMoveMessage(index, 1)}
                      >
                        <ArrowDownward />
                      </IconButton>
                      <IconButton
                        title="Meldung bearbeiten"
                        onClick={() => setMessageForm({ publication: draft, message, isPublished: false })}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton title="Meldung löschen" onClick={() => setDeleteState({ publication: draft, message })}>
                        <DeleteOutlineOutlined />
                      </IconButton>
                    </>
                  }
                >
                  <ListItemText
                    primary={<Typography variant={"h4"} sx={{ marginBottom: "0.5rem" }}>{message.headline}</Typography>}
                    secondary={
                      <>
                        <Typography component={"div"} color={"textSecondary"}>
                          {message.productArea} · {message.topic}
                        </Typography>
                        <Typography component={"div"} color={"textSecondary"}>
                          {message.createdBy?.person?.fullname ?? "Unbekannt"} · gespeichert am {dayjs(message.updated).format("DD.MM.YYYY HH:mm")}
                        </Typography>
                      </>
                    }
                    slotProps={{
                      primary: {
                        component: "div"
                      },
                      secondary: {
                        component: "div"
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
          <Button
            variant="text"
            sx={releaseUpdateStyles.addMessage}
            onClick={() => setMessageForm({ publication: draft, message: null, isPublished: false })}
          >
            <Add /> Meldung hinzufügen
          </Button>
          <Stack direction={"row"} sx={releaseUpdateStyles.actions}>
           <Box>
             <Typography>Sichtbarkeit des Release-Updates</Typography>
             <Grid container spacing={2}>
               <Grid>
                 <FormControlLabel
                   control={
                     <Checkbox
                       checked={draft.visibleToInternal}
                       disabled={isVisibilitySaving}
                       onChange={(event) => handleVisibilityChange(event.target.checked, draft.visibleToExternal)}
                     />
                   }
                   label="Intern"
                 />
               </Grid>
               <Grid>
                 <FormControlLabel
                   control={
                     <Checkbox
                       checked={draft.visibleToExternal}
                       disabled={isVisibilitySaving}
                       onChange={(event) => handleVisibilityChange(draft.visibleToInternal, event.target.checked)}
                     />
                   }
                   label="Extern"
                 />
               </Grid>
             </Grid>
           </Box>
            <Grid container spacing={2} sx={releaseUpdateStyles.managementActions}>
              <Grid>
                <Button variant={"outlined"} disabled={draft.messages.length === 0} onClick={() => openReleaseUpdatePreview(draft)}>
                  Vorschau
                </Button>
              </Grid>
              <Grid>
                <Button variant="contained" disabled={draft.messages.length === 0} onClick={handleReviewPublication}>
                  Veröffentlichung prüfen
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </>
      )}

      {selectedTab === 1 && !selectedPublication && (
        <>
          <Typography variant={"h2"} sx={releaseUpdateStyles.headline}>Release-Verlauf</Typography>
          <Typography sx={releaseUpdateStyles.subline}>Alle veröffentlichten Release-Updates</Typography>
          {published.length === 0 ? (
            <Alert severity="info">Es wurden noch keine Release-Updates im neuen Format veröffentlicht.</Alert>
          ) : (
            <List>
              {published.map((publication) => (
                <ListItem
                  key={publication.id}
                  sx={{ padding: "0.5rem 0" }}
                  divider
                  disablePadding
                  secondaryAction={<Button onClick={() => handleOpenPublished(publication.id)} variant={"outlined"}>Ansehen</Button>}
                >
                  <ListItemText
                    primary={`Release-Update vom ${dayjs(publication.publishedAt ?? publication.created).format("DD.MM.YYYY")}`}
                    secondary={`${publication.messages.length} Meldung${publication.messages.length === 1 ? "" : "en"} · veröffentlicht um ${dayjs(publication.publishedAt ?? publication.created).format("HH:mm")}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
          {publishedTotalPages > 1 && (
            <Grid container sx={{ justifyContent: "flex-end" }}>
              <PagePagination
                currentPage={publishedPage}
                totalPages={publishedTotalPages}
                handlePrevPage={() => handlePublishedPageChange(publishedPage - 1)}
                handleNextPage={() => handlePublishedPageChange(publishedPage + 1)}
              />
            </Grid>
          )}
        </>
      )}

      {selectedTab === 1 && selectedPublication && (
        <>
          <Button onClick={() => setSelectedPublication(null)} sx={{ marginBottom: "2rem", width: "auto" }}>
            <ArrowBack sx={{ marginRight: "0.5rem"}} /> Zurück zur Übersicht
          </Button>
          <Typography variant="h2" sx={{ marginBottom: "1rem" }}>
            Release-Update vom {dayjs(selectedPublication.publishedAt ?? selectedPublication.created).format("DD.MM.YYYY")}
          </Typography>
          {selectedPublication.messages.length === 0 && (
            <Alert severity="info">Dieses Release-Update enthält keine sichtbaren Meldungen mehr.</Alert>
          )}
          {selectedPublication.messages.map((message, index) => (
            <section key={message.id}>
              {index > 0 && <Divider sx={{ marginY: "2rem" }} />}
              <Grid container spacing={1} sx={{ justifyContent: "flex-end", marginBottom: "1rem" }}>
                <Grid>
                  <Button
                    startIcon={<Edit />}
                    onClick={() => setMessageForm({ publication: selectedPublication, message, isPublished: true })}
                  >
                    Bearbeiten
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    color="error"
                    startIcon={<DeleteOutlineOutlined />}
                    onClick={() => setDeleteState({ publication: selectedPublication, message })}
                  >
                    Löschen
                  </Button>
                </Grid>
              </Grid>
              <ReleaseUpdateContent
                message={message}
                publishedAt={selectedPublication.publishedAt}
                mediaObjects={selectedMediaObjects}
              />
            </section>
          ))}
        </>
      )}

      {messageForm && (
        <ReleaseUpdateMessageForm
          publication={messageForm.publication}
          message={messageForm.message}
          isPublished={messageForm.isPublished}
          onClose={() => setMessageForm(null)}
          onSaved={handleMessageSaved}
        />
      )}
      <LegalbirdIoConfirm
        open={deleteState !== null}
        handleClose={() => setDeleteState(null)}
        handleConfirm={handleDeleteMessage}
        headline="Meldung löschen"
        confirmText="Meldung löschen"
        content={
          <>
            <Typography>{deleteState?.message.headline}</Typography>
            <Typography sx={{ marginTop: "1rem" }}>
              Die Meldung wird aus Ausspielung und Verlauf entfernt. Ist sie die letzte Meldung, wird das gesamte
              Release-Update für Nutzer ausgeblendet.
            </Typography>
          </>
        }
      />
      <LegalbirdIoConfirm
        open={reviewedDraft !== null}
        handleClose={() => setReviewedDraft(null)}
        handleConfirm={handlePublish}
        headline="Veröffentlichung prüfen"
        confirmText="Jetzt veröffentlichen"
        content={
          <Box>
            <Typography>{
              reviewedDraft?.messages.length === 1 ? `Diese gespeicherte Meldung wird veröffentlicht` : `Diese ${reviewedDraft?.messages.length} gespeicherten Medungen werden gemeinsam veröffentlicht`
            }</Typography>
            <List dense>
              {reviewedDraft?.messages.map((message, idx) => (
                <ListItem key={message.id} disablePadding >
                  <ListItemText primary={`${idx + 1}. ${message.headline}`} />
                </ListItem>
              ))}
            </List>
          </Box>
        }
      />
    </ContentBox>
  );
}
